// The composed screens of /my-data: what the kept entries add up to.
//
// The browse screen lists entries one by one. These screens read ACROSS them: the Article 30
// register the privacy records form fills, and the ADR journal the architecture records form fills.
// Each is a view of the read model, recomputed on every load and every change, and exported as a
// text file — nothing is stored for them and nothing leaves the browser.
//
// THERE WERE THREE MORE, and they went with their source. The families (my cameras, my places, my
// persons, my IBANs), the day counters read again today, and the planner across the kept places all
// read entries that only the « keep this in my browser » button on a tool's answer could write.
// That button is gone, and a screen whose source cannot produce a single row is not an empty screen
// — it is a promise the page cannot keep.
//
// EACH SCREEN APPEARS ONLY WHEN THE STORE HOLDS SOMETHING OF ITS KIND — and, once shown, stays for
// the rest of the visit, empty, rather than vanish: a section that collapses ABOVE the list pulls
// everything below it up under the pointer at the exact moment a "forget" was just pressed in the
// list. The same rule the toolbar follows, for the same measured reason.
//
// The same discipline as the browse screen: no state of its own, every change is a command, and
// the redraw comes from the event the write model announces — here or in another tab.

import { all, subscribe } from './write-model.js';
import { KEY } from './store.js';
import { register, journal } from './records.js';
import { drawTable, exportBar, fileStem } from './table.js';
import { words } from './words.js';

const root = document.getElementById('mydata');

if (root) {
	start(root);
}

function start(root) {
	const entries = document.getElementById('mydata-entries');
	const labels = words(root);

	const container = document.createElement('div');
	container.className = 'mydata-screens';
	container.id = 'mydata-screens';
	// Above the entry list, below the outcome line — so the outcome, and the undo it offers, never
	// moves when a screen is redrawn.
	entries.parentNode.insertBefore(container, entries);

	/* The screens ever shown this visit. See the note at the top: shown once, kept. */
	const shown = new Set();

	/* A screen: a section with a heading. Returns null when there is nothing to show AND the screen
	   has never been shown; the caller then draws nothing. */
	function screen(key, title, hasContent) {
		if (!hasContent && !shown.has(key)) return null;
		shown.add(key);
		const section = document.createElement('section');
		section.className = 'mydata-screen';
		section.dataset.screen = key;
		const heading = document.createElement('h2');
		heading.className = 'mydata-screen-title';
		heading.textContent = title;
		section.append(heading);
		if (!hasContent) {
			const empty = document.createElement('p');
			empty.className = 'mydata-screen-empty';
			empty.textContent = labels.nothingLeft;
			section.append(empty);
		}
		return section;
	}

	// ── The register and the journal ──────────────────────────────────────────────────────────

	function registerScreen(kept) {
		const table = register(kept);
		const section = screen('register', labels.registerTitle, table.rows.length > 0);
		if (!section || !table.rows.length) return section;
		section.append(drawTable(table), exportBar(table, fileStem(labels.registerTitle), labels));
		return section;
	}

	function journalScreen(kept) {
		const { decisions, references } = journal(kept, labels);
		const section = screen('journal', labels.journalTitle, decisions.rows.length > 0 || references.rows.length > 0);
		if (!section || (!decisions.rows.length && !references.rows.length)) return section;
		if (decisions.rows.length) {
			section.append(drawTable(decisions), exportBar(decisions, fileStem(labels.journalTitle), labels));
		}
		if (references.rows.length) {
			const heading = document.createElement('h3');
			heading.className = 'mydata-screen-subtitle';
			heading.textContent = labels.referencesTitle;
			section.append(heading, drawTable(references),
				exportBar(references, fileStem(labels.referencesTitle), labels));
		}
		return section;
	}


	// ── Read, project, draw ───────────────────────────────────────────────────────────────────

	function render(kept) {
		container.textContent = '';
		[registerScreen, journalScreen].forEach((build) => {
			const section = build(kept);
			if (section) container.append(section);
		});
	}

	function refresh() {
		return all().then(render);
	}

	subscribe(() => refresh());
	window.addEventListener('storage', (event) => {
		if (event.key === KEY) refresh();
	});
	refresh();
}
